import { env } from "../../config/env";
import { authStorage } from "../../lib/storage/authStorage";
import { apiClient } from "../../lib/http/apiClient";

export interface AnalyticsEventPayload {
  event_type: "contact_click" | "save_for_later" | "unsave_for_later" | "cross_sell_carousel_viewed" | "cross_sell_card_clicked" | "cross_sell_enquiry_sent";
  channel?: "whatsapp" | "email" | "save_for_later";
  source: string;
  item_id: string;
  item_type: string;
  item_title?: string;
  seller_id?: string;
  seller_name?: string;
  seller_whatsapp?: string;
  seller_email?: string;
  destination_url?: string;
  session_id?: string;
  metadata?: Record<string, unknown>;
}

// ── Legacy response types (GET /api/analytics/seller) ───────────────────────

export interface SellerAnalyticsBuyer {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  whatsapp_number?: string;
}

export interface SellerAnalyticsEvent {
  event_type: string;
  channel?: string;
  source: string;
  item_id: string;
  item_type: string;
  item_title?: string;
  created_at?: string;
  buyer?: SellerAnalyticsBuyer | null;
  metadata?: Record<string, unknown>;
}

export interface SellerAnalyticsItemSummary {
  item_id: string;
  item_type: string;
  item_title?: string;
  contact_clicks: number;
  whatsapp_clicks: number;
  email_clicks: number;
  saves: number;
  unsaves: number;
  total_events: number;
}

export interface SellerAnalyticsSummary {
  total_events: number;
  whatsapp_clicks: number;
  email_clicks: number;
  saves: number;
  unsaves: number;
  unique_buyers: number;
  anonymous_events: number;
}

export interface SellerAnalyticsResponse {
  summary: SellerAnalyticsSummary;
  top_items: SellerAnalyticsItemSummary[];
  recent_events: SellerAnalyticsEvent[];
}

// ── New dashboard types (GET /api/analytics/seller/dashboard) ────────────────

export interface DailyRow {
  date: string;
  seller_id?: string;
  deal_views: number;
  deal_views_unique: number;
  deal_likes: number;
  deal_saves: number;
  deal_unsaves: number;
  enquiries: number;
  leads_accepted: number;
  leads_contacted?: number;
  leads_closed?: number;
  dm_threads_opened: number;
  whatsapp_clicks: number;
  email_clicks: number;
  cross_sell_carousel_views?: number;
  cross_sell_card_clicks?: number;
  cross_sell_enquiries?: number;
  news_views?: number;
  news_likes?: number;
  webinar_views?: number;
  webinar_likes?: number;
  ai_flyers_generated?: number;
  ai_videos_generated?: number;
  ai_itineraries_generated?: number;
  ai_slides_generated?: number;
  ai_tokens_used?: number;
}

export interface DealMetric {
  deal_id: string;
  seller_id: string;
  item_title?: string;
  item_type?: string;
  views_total: number;
  views_unique: number;
  likes_total: number;
  saves_total: number;
  unsaves_total: number;
  enquiries_total: number;
  leads_accepted_total: number;
  dm_threads_total: number;
  whatsapp_clicks_total: number;
  email_clicks_total: number;
}

export interface DashboardTotals {
  deal_views: number;
  deal_views_unique: number;
  deal_likes: number;
  deal_saves: number;
  deal_unsaves: number;
  enquiries: number;
  leads_accepted: number;
  leads_contacted?: number;
  leads_closed?: number;
  dm_threads_opened: number;
  whatsapp_clicks: number;
  email_clicks: number;
  news_views: number;
  news_likes: number;
  webinar_views: number;
  webinar_likes: number;
  ai_flyers_generated: number;
  ai_videos_generated: number;
  ai_itineraries_generated: number;
  ai_slides_generated: number;
  ai_tokens_used: number;
}

export interface SellerDashboardResponse {
  days: DailyRow[];
  totals: DashboardTotals;
  today_live: Partial<DashboardTotals>;
}

export const analyticsService = {
  trackEvent(payload: AnalyticsEventPayload): Promise<void> {
    const token = authStorage.getToken();
    return fetch(`${env.apiBaseUrl}/api/analytics/event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(() => undefined)
      .catch(() => undefined);
  },

  trackContactClick(payload: Omit<AnalyticsEventPayload, "event_type">): Promise<void> {
    return this.trackEvent({ ...payload, event_type: "contact_click" });
  },

  trackSaveForLater(payload: Omit<AnalyticsEventPayload, "event_type" | "channel">): Promise<void> {
    return this.trackEvent({ ...payload, event_type: "save_for_later", channel: "save_for_later" });
  },

  trackUnsaveForLater(payload: Omit<AnalyticsEventPayload, "event_type" | "channel">): Promise<void> {
    return this.trackEvent({ ...payload, event_type: "unsave_for_later", channel: "save_for_later" });
  },

  async getSellerAnalytics(): Promise<SellerAnalyticsResponse> {
    const { data } = await apiClient.get<SellerAnalyticsResponse>("/api/analytics/seller");
    return data;
  },

  async getSellerDashboard(days = 30): Promise<SellerDashboardResponse> {
    const { data } = await apiClient.get<SellerDashboardResponse>(`/api/analytics/seller/dashboard?days=${days}`);
    return data;
  },
};

// ── Session helpers ───────────────────────────────────────────────────────────

function _getSessionId(): string {
  let sid = sessionStorage.getItem("_gsid");
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem("_gsid", sid);
  }
  return sid;
}

function _getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "web";
}

function _getActorRole(): string {
  return authStorage.getUser()?.role ?? "";
}

function _basePayload() {
  return {
    session_id: _getSessionId(),
    actor_role: _getActorRole(),
    device_type: _getDeviceType(),
  };
}

function _fire(path: string, body: Record<string, unknown>): void {
  const token = authStorage.getToken();
  fetch(`${env.apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ..._basePayload(), ...body }),
    keepalive: true,
  }).catch(() => undefined);
}

// ── New signal-capture methods ────────────────────────────────────────────────

export const trackingService = {
  sessionStart(platform = "web", appVersion = ""): void {
    _fire("/api/analytics/session/start", { platform, app_version: appVersion });
  },

  sessionEnd(durationMs: number, pagesVisited: string[] = []): void {
    _fire("/api/analytics/session/end", { duration_ms: durationMs, pages_visited: pagesVisited });
  },

  pageView(page: string, referrer = "", durationMs = 0): void {
    _fire("/api/analytics/page-view", { page, referrer, duration_ms: durationMs });
  },

  scrollDepth(page: string, scrollPct: number): void {
    _fire("/api/analytics/scroll", { page, scroll_pct: scrollPct });
  },

  filterApplied(filters: Record<string, unknown>, resultCount = 0): void {
    _fire("/api/analytics/filter", { filters, result_count: resultCount });
  },

  searchQuery(query: string, source = "search_bar", resultCount = 0): void {
    _fire("/api/analytics/search", { query, source, result_count: resultCount });
  },

  flyerAction(action: "downloaded" | "shared" | "regenerated", opts: { mediaJobId?: string; dealId?: string; mediaType?: string; destinations?: string[] } = {}): void {
    _fire("/api/analytics/flyer-action", {
      action,
      media_job_id: opts.mediaJobId ?? "",
      deal_id: opts.dealId ?? "",
      media_type: opts.mediaType ?? "flyer",
      destinations: opts.destinations ?? [],
    });
  },

  ragQuery(query: string, ragSessionId = "", opts: { responseMs?: number; resultsCount?: number; queryIntent?: string; tab?: string } = {}): void {
    _fire("/api/analytics/rag-query", {
      query,
      rag_session_id: ragSessionId,
      response_ms: opts.responseMs ?? 0,
      results_count: opts.resultsCount ?? 0,
      query_intent: opts.queryIntent ?? "",
      tab: opts.tab ?? "",
    });
  },

  profileView(profileId: string, profileType: "seller" | "buyer" = "seller"): void {
    _fire("/api/analytics/profile-view", { profile_id: profileId, profile_type: profileType });
  },

  notificationAction(notificationId: string, action: "opened" | "dismissed" | "cta_clicked", notificationType = ""): void {
    _fire("/api/analytics/notification-action", { notification_id: notificationId, action, notification_type: notificationType });
  },

  tabSwitch(fromTab: string, toTab: string, sessionId = ""): void {
    _fire("/api/analytics/tab-switch", { from_tab: fromTab, to_tab: toTab, session_id: sessionId });
  },

  dealView(dealId: string, sellerId: string, opts: { categorySlug?: string; campaignId?: string } = {}): void {
    _fire("/api/analytics/deal-view", {
      deal_id: dealId,
      seller_id: sellerId,
      category_slug: opts.categorySlug ?? "",
      campaign_id: opts.campaignId ?? "",
    });
  },

  ingest(eventType: string, itemId: string, sellerId: string, opts: { channel?: string; source?: string; campaignId?: string } = {}): void {
    _fire("/api/analytics/ingest", {
      event_type: eventType,
      item_id: itemId,
      seller_id: sellerId,
      channel: opts.channel ?? "",
      source: opts.source ?? "",
      campaign_id: opts.campaignId ?? "",
    });
  },
};
